import { useEffect, useState, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../stores/auth.store';
import { Sidebar } from './Sidebar';
import { Loader2, Menu } from 'lucide-react';
import { Button } from './ui/button';
import { useRouteRefresh } from '@/hooks/useRouteRefresh';
import { useLogout } from '@/hooks/useLogout';

export function DashboardLayout() {
    const cerrarSesion = useLogout();
    const { user, setUser } = useAuthStore();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const location = useLocation();

    // Cerrar sidebar móvil al navegar
    useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

    // Forzar refetch de datos al navegar entre rutas del sidebar
    useRouteRefresh();

    // Obtener perfil del usuario al montar el componente
    const { data: profile } = useQuery({
        queryKey: ['profile'],
        queryFn: authService.getProfile,
        retry: false,
        staleTime: 0, // Siempre considerar los datos como stale
    });

    useEffect(() => {
        if (profile) {
            setUser(profile);
        }
    }, [profile, setUser]);

    // El cierre completo (servidor, cache de React Query, store y login) vive en useLogout,
    // compartido con la guarda de RoleRoute; acá solo se agrega el aviso de éxito.
    const handleLogout = async () => {
        const cerradaEnServidor = await cerrarSesion();
        if (cerradaEnServidor) {
            toast.success('Sesión cerrada correctamente');
        }
    };

    const toggleSidebar = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    return (
        <div className="flex h-dvh overflow-hidden bg-background">
            {/* Overlay mobile */}
            {mobileNavOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/50 lg:hidden"
                    onClick={() => setMobileNavOpen(false)}
                />
            )}

            {/* Sidebar — drawer en mobile, fijo en desktop */}
            <div className={`
                fixed inset-y-0 left-0 z-40 lg:relative lg:z-auto
                transition-transform duration-300 ease-in-out
                ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <Sidebar
                    user={user}
                    onLogout={handleLogout}
                    collapsed={sidebarCollapsed}
                    onToggle={toggleSidebar}
                />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Top bar mobile */}
                <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b bg-card flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setMobileNavOpen(true)}>
                        <Menu className="h-5 w-5" />
                    </Button>
                    <span className="text-lg font-black tracking-tight leading-none select-none" aria-label="PosBIT">
                        <span className="text-black dark:text-white">POS</span><span style={{ color: '#FFC72C' }}>BIT</span>
                    </span>
                </div>

                <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 xl:p-8">
                    <div className="max-w-9xl mx-auto">
                        <Suspense fallback={
                            <div className="flex h-full w-full items-center justify-center p-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        }>
                            <Outlet />
                        </Suspense>
                    </div>
                </main>
            </div>
        </div>
    );
}
